import React, { FC, memo } from 'react';
import { Log, LogsTypes } from '../logs.types';
import cls from './LogDescription.module.scss';
import { CustomOverlay } from '../LogPreviewItem/LogPreviewItem';
import { convertDateToNeedFormat } from '../../../utils/convertDateHandlers';

type LogDescriptionProps = {
  log: Log;
  logsType: LogsTypes[];
  description: string;
  setObjectIdHandler: ({ id, email }: { id: string; email: string }) => void;
};

/** Shown in place of a name the backend could not resolve, e.g. of an object deleted since */
export const UNKNOWN_NAME = '—';

/**
 * The placeholders eventlogger puts into its templates. Split on them, not on spaces: a placeholder may have
 * punctuation glued to it (`${objectName}:`) and `${description}` may stand mid-sentence.
 */
const PLACEHOLDER = /(\$\{(?:subjectName|objectName|objectId|description)\})/;

/** A date-time apisupport wrote into a description: UTC, with `T` or a space, the zone usually left out */
const SERVER_DATE_TIME = /\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?Z?/g;

/** The list shows local time, so the dates inside a description are shown in local time too */
export const localizeDescription = (description: string) =>
  description.replace(SERVER_DATE_TIME, (value) =>
    convertDateToNeedFormat({ date: value, format: 'datetime' }),
  );

const LogDescription: FC<LogDescriptionProps> = memo(
  ({ log, logsType, description, setObjectIdHandler }) => {
    const actualLogTemplate = Object.values(logsType).find((elem) => elem?.type === log.type);
    if (!actualLogTemplate) {
      return null;
    }
    const { subjectLink, objectLink } = actualLogTemplate;
    const parts = actualLogTemplate.template.split(PLACEHOLDER);
    if (!description) {
      // a template ending in ": ${description}" would otherwise end in a dangling colon
      const last = parts.length - 1;
      if (parts[last] === '' && parts[last - 1] === '${description}') {
        parts[last - 2] = parts[last - 2].replace(/:\s*$/, '');
      }
    }

    const renderAccount = (key: number, id?: string, name?: string, link?: string) => {
      if (!id) {
        return <React.Fragment key={key}>{name || UNKNOWN_NAME}</React.Fragment>;
      }
      const shownName = name || UNKNOWN_NAME;
      return (
        <CustomOverlay
          key={key}
          name={shownName}
          link={link ?? ''}
          id={id}
          clickHandler={() => setObjectIdHandler({ id, email: name ?? id })}
        />
      );
    };

    return (
      <div className={`${cls.Description} regular-text`}>
        {parts.map((part, index) => {
          switch (part) {
            case '${subjectName}':
              return renderAccount(index, log.subjectId, log.subjectName, subjectLink);
            case '${objectName}':
              return renderAccount(index, log.objectId, log.objectName, objectLink);
            case '${objectId}':
              return renderAccount(index, log.objectId, log.objectId, objectLink);
            case '${description}':
              return (
                <React.Fragment key={index}>
                  {localizeDescription(description ?? '')}
                </React.Fragment>
              );
            default:
              return <React.Fragment key={index}>{part}</React.Fragment>;
          }
        })}
      </div>
    );
  },
);

export default LogDescription;
